import { Loader2, MaximizeIcon, RefreshCw } from "lucide-react";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { ContentCopyButton } from "~/components/ContentCopyButton";
import { MarkdownContentModal } from "~/components/MarkdownContentModal";
import { MarkdownExportButton } from "~/components/MarkdownExportButton";
import { TabControlBar } from "~/components/TabControlBar";
import { Button } from "~/components/ui/button";
import { Skeleton } from "~/components/ui/skeleton";
import { UnifiedMarkdown } from "~/components/UnifiedMarkdown";
import { useParentCase } from "~/features/case/hooks/useParentCase";
import { formatDate, useChatDocumentParams } from "~/helpers";
import { useMobile } from "~/hooks/use-mobile";
import { useDocumentSearchParams } from "~/hooks/useDocumentSearchParams";
import { trackEvent } from "~/utils/analytics";
import { useSourceMapDelegation } from "~/utils/markdownTerms";
import { formatCaseInfo, getFileTitle } from "../helpers";
import { useBatesLinks } from "../hooks/useBatesLinks";
import { useDocList } from "../hooks/useDocList";
import { useFormatSummary } from "../hooks/useFormatSummary";
import { useSectionLinks } from "../hooks/useSectionLinks";
import { useSectionSummary } from "../hooks/useSectionSummary";
import { useSelectedEntityStore } from "../stores/selectedEntityStore";
import { FormatSectionInput } from "./reportArea/FormatSectionInput";
import { useReportTerms } from "./reportArea/useReportTerms";

export const SummaryArea: FC = () => {
  const [{ sectionId, pageId }, updateParam] = useDocumentSearchParams();
  const { caseId } = useChatDocumentParams();
  const { docList } = useDocList();
  const sectionLinks = useSectionLinks();
  const batesLinks = useBatesLinks();
  const { project } = useParentCase();
  const caseInfo = project ? formatCaseInfo(project) : undefined;
  const { formatMessage } = useIntl();
  const { isMobile } = useMobile();
  const [maximizeModalOpen, setMaximizeModalOpen] = useState(false);
  const selectedEntity = useSelectedEntityStore(
    (state) => state.selectedEntity
  );
  const setSelectedEntity = useSelectedEntityStore(
    (state) => state.setSelectedEntity
  );

  const terms = useReportTerms(caseId);

  const selectedSection = useMemo(() => {
    if (!docList) return null;

    if (!selectedEntity) {
      return null;
    }

    if (selectedEntity.entityType === "page") {
      return docList.tree
        .flatMap((doc) => doc.sections)
        .find((section) => section.pageIds?.includes(selectedEntity.entityId));
    } else if (selectedEntity.entityType === "section") {
      return docList.tree
        .flatMap((doc) => doc.sections)
        .find((section) => section.id === selectedEntity.entityId);
    }

    return null;
  }, [selectedEntity, docList]);

  const selectedPage = useMemo(() => {
    if (!selectedEntity || !selectedSection) return null;
    return selectedSection.pages?.find(
      (page) => page.id === selectedEntity.entityId
    );
  }, [selectedEntity, selectedSection]);

  const sectionName = selectedSection?.name || "Unknown Section";

  const {
    summary,
    status,
    error,
    regenerate,
    canRegenerate,
    isRegenerating,
    regenerateError,
  } = useSectionSummary();

  const {
    format: formatSummary,
    canFormat,
    isFormatting,
    formatError,
    debouncedEntity: formatDebouncedEntity,
  } = useFormatSummary();

  const handleFormatSummary = useCallback(
    (instructions: string) => {
      if (!summary) return;
      formatSummary(summary, instructions);
    },
    [summary, formatSummary]
  );

  useEffect(() => {
    if (!selectedEntity && docList?.tree && docList.tree.length > 0) {
      const firstSection = docList.tree[0]?.sections?.[0];
      if (firstSection) {
        setSelectedEntity(
          "section",
          firstSection.id,
          firstSection.pageIds || []
        );
      }
    }
  }, [selectedEntity, docList, setSelectedEntity]);

  useEffect(() => {
    if (!sectionId && !pageId && docList?.tree && docList.tree.length > 0) {
      const firstSection = docList.tree[0]?.sections?.[0];
      if (firstSection) {
        updateParam({ sectionId: firstSection.id });
      }
    }
  }, [sectionId, pageId, docList, updateParam]);

  const handleSectionClick = useCallback(
    (clickedSectionId: string, firstPageId: string) => {
      const section = docList?.tree
        .flatMap((category) => category.sections)
        .find((s) => s.id === clickedSectionId);
      if (section) {
        setSelectedEntity("section", clickedSectionId, section.pageIds || []);
      }
      updateParam({
        sectionId: clickedSectionId,
        pageId: firstPageId,
      });
    },
    [updateParam, docList, setSelectedEntity]
  );

  const handleBatesClick = useCallback(
    (batesPageId: string) => {
      updateParam({ pageId: batesPageId });
    },
    [updateParam]
  );

  const [summaryContainerEl, setSummaryContainerEl] =
    useState<HTMLDivElement | null>(null);
  useSourceMapDelegation(
    summaryContainerEl,
    handleSectionClick,
    handleBatesClick
  );

  const displayTitle = useMemo(() => {
    if (selectedEntity?.entityType === "page" && selectedPage) {
      const pageNumber = selectedPage.originPageNum + 1;
      return `${sectionName} - Page ${pageNumber}`;
    }
    return sectionName;
  }, [selectedEntity?.entityType, selectedPage, sectionName]);

  const exportFileName = useMemo(() => {
    const title = getFileTitle(displayTitle);
    return `Summary "${title}" (${formatDate(new Date().toISOString())})`;
  }, [displayTitle]);

  const isEmpty = !summary && status !== "loading";

  const lastParagraph = useMemo(() => {
    if (!summary) return null;
    const trimmed = summary.trim();
    const paragraphs = trimmed.split("\n\n");
    return paragraphs[paragraphs.length - 1]?.trim() || null;
  }, [summary]);

  const endsWithQuestion = lastParagraph?.endsWith("?");

  const handleAskInChat = () => {
    if (lastParagraph) {
      updateParam({ tab: "chat" });
    }
  };

  const showFooter =
    !isEmpty &&
    status !== "loading" &&
    status !== "error" &&
    (sectionId || pageId);

  return (
    <>
      <div
        className={`relative ${isMobile ? "flex flex-col h-docsidebar-content-mobile" : ""}`}
      >
        <div
          ref={setSummaryContainerEl}
          className={`p-3 border-zinc-100 overflow-y-auto overflow-x-hidden ${isMobile ? "flex-1" : "h-[calc(100vh-92px)]"}`}
        >
          {!sectionId && !pageId ? (
            <div className="flex items-center justify-center h-full">
              <p className="text-muted-foreground text-sm">
                <FormattedMessage
                  id="nativeDocViewer.noCategorySelected"
                  defaultMessage="Please pick a section on the left to preview its content"
                />
              </p>
            </div>
          ) : status === "loading" ? (
            <div className="space-y-4 animate-pulse">
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-5/6" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-4/5" />
            </div>
          ) : status === "error" ? (
            <div className="flex flex-col items-center justify-center h-full gap-4">
              <p className="text-red-500 text-sm">
                {error ||
                  formatMessage({
                    id: "chat.summary.error",
                    defaultMessage:
                      "Failed to generate summary. Please try again.",
                  })}
              </p>
            </div>
          ) : isEmpty ? (
            <div className="flex items-center justify-center h-full">
              <p className="text-muted-foreground text-sm">
                <FormattedMessage
                  id="chat.summary.noContent"
                  defaultMessage="No summary available for this section"
                />
              </p>
            </div>
          ) : (
            <div className="group flex flex-col gap-4">
              <div>
                <h2 className="text-sm font-semibold mb-2">
                  {displayTitle.toUpperCase()}
                </h2>
                <UnifiedMarkdown withHeadings>{summary ?? ""}</UnifiedMarkdown>
              </div>
              {endsWithQuestion && (
                <div className="not-prose">
                  <Button onClick={handleAskInChat} variant="outline" size="sm">
                    <FormattedMessage
                      id="chat.summary.askInChat"
                      defaultMessage="Ask in Chat"
                    />
                  </Button>
                </div>
              )}
              {regenerateError && (
                <p className="text-red-500 text-sm" role="alert">
                  {regenerateError}
                </p>
              )}
              {formatError && (
                <p className="text-red-500 text-sm" role="alert">
                  {formatError}
                </p>
              )}
              <div className="h-8 invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-opacity duration-200 mt-0">
                <div className="inline-flex items-center gap-2">
                  <ContentCopyButton
                    content={summary ?? ""}
                    preCopyMessageId="copy"
                    color="muted-foreground"
                    eventName="app/doc_sidebar/summary/copy.button/click"
                  />
                  <Button
                    variant="ghost"
                    size="mini"
                    className="text-muted-foreground hover:text-muted-foreground inline-flex items-center px-0 text-xs hover:bg-transparent"
                    onClick={regenerate}
                    disabled={!canRegenerate || isFormatting}
                    title={regenerateError ?? undefined}
                  >
                    {isRegenerating ? (
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4 mr-1" />
                    )}
                    <FormattedMessage
                      id="report.rewrite"
                      defaultMessage="Rewrite"
                    />
                  </Button>
                  {formatDebouncedEntity && (
                    <FormatSectionInput
                      sectionId={formatDebouncedEntity.entityId}
                      sectionTitle={displayTitle}
                      isFormatting={isFormatting}
                      disabled={!canFormat || !summary || isRegenerating}
                      onFormat={handleFormatSummary}
                      analyticsEventName="app/doc_sidebar/summary/format.button/click"
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        <p className="text-center text-[10px] text-muted-foreground py-1 shrink-0 whitespace-nowrap">
          <FormattedMessage id="aiDisclaimer" />
        </p>
        <TabControlBar
          visible={!!showFooter}
          className={isMobile ? "flex-shrink-0" : ""}
        >
          <TabControlBar.Group>
            <Button
              variant="ghost"
              size="mini"
              onClick={() => {
                trackEvent({
                  eventName: "app/doc_sidebar/summary/maximize.button/click",
                });
                setMaximizeModalOpen(true);
              }}
            >
              <MaximizeIcon className="w-4 h-4 mr-2" />
              <FormattedMessage
                id="common.maximize"
                defaultMessage="Maximize"
              />
            </Button>
            <ContentCopyButton
              content={summary || ""}
              preCopyMessageId="copyAll"
              size="mini"
              eventName="app/doc_sidebar/summary/copy_all.button/click"
            />
            <MarkdownExportButton
              content={summary || ""}
              fileName={exportFileName}
              buttonSize="mini"
              openEventName="app/doc_sidebar/summary/export.button/click"
              exportEventName="app/doc_sidebar/summary/export_menu_open/export_action.link/click"
              caseInfo={caseInfo}
            />
          </TabControlBar.Group>
        </TabControlBar>
      </div>
      {maximizeModalOpen && (
        <MarkdownContentModal
          title={formatMessage({ id: `chat.summary.title` })}
          filename={exportFileName}
          content={summary ?? ""}
          open={maximizeModalOpen}
          onOpenChange={setMaximizeModalOpen}
          copyEventName={`app/summary_maximize_modal/copy_all.button/click`}
          exportOpenEventName={`app/summary_maximize_modal/export.button/click`}
          exportEventName={`app/summary_maximize_modal/export_menu_open/export_action.link/click`}
          terms={terms}
          caseInfo={caseInfo}
          sectionLinks={sectionLinks}
          onSectionClick={handleSectionClick}
          batesLinks={batesLinks}
          onBatesClick={handleBatesClick}
        />
      )}
    </>
  );
};
